/* eslint no-console: ["error", { allow: ["warn", "error"] }] */
import ApiService from '@services/apiService';

import {
  getSortedDefinition,
  transformDefinition,
} from '@redux/modules/formGeneration';

const CLEAR_OFFICE = 'p9/office/CLEAR_OFFICE';
const CLEAR_OFFICE_SEARCH = 'p9/office/CLEAR_OFFICE_SEARCH';

const HOME_BUTTON_CLICKED = 'HOME_BUTTON_CLICKED';
const LOAD_FORM_DEFINITION = 'LOAD_FORM_DEFINITION';

const LOAD_OFFICE_DEFINITION = 'p9/office/LOAD_OFFICE_DEFINITION';
const LOAD_OFFICE_DEFINITION_FAIL = 'p9/office/LOAD_OFFICE_DEFINITION_FAIL';
const LOAD_OFFICE_DEFINITION_SUCCESS = 'pr/office/LOAD_OFFICE_DEFINITION_SUCCESS';

const FORM_SUBMITTED = 'FORM_SUBMITTED';

const OFFICE_LOOKUP = 'OFFICE_LOOKUP';
const OFFICE_LOOKUP_FAIL = 'OFFICE_LOOKUP_FAIL';
const OFFICE_LOOKUP_SUCCESS = 'OFFICE_LOOKUP_SUCCESS';

const OFFICE_PAGE_CLICKED = 'OFFICE_PAGE_CLICKED';

const SEARCH_LISTINGS_PAGE_CLICKED = 'SEARCH_LISTINGS_PAGE_CLICKED';

const SEARCH_OFFICES = 'p9/office/SEARCH_OFFICES';
const SEARCH_OFFICES_FAIL = 'p9/office/SEARCH_OFFICES_FAIL';
const SEARCH_OFFICES_SUCCESS = 'p9/office/SEARCH_OFFICES_SUCCESS';

const SELECT_OFFICE = 'p9/office/SELECT_OFFICE';
const SELECT_OFFICE_FAIL = 'p9/office/SELECT_OFFICE_FAIL';
const SELECT_OFFICE_SUCCESS = 'p9/office/SELECT_OFFICE_SUCCESS';

const SELECT_OFFICE_LOOKUP = 'p9/office/SELECT_OFFICE_LOOKUP';
const SELECT_OFFICE_LOOKUP_FAIL = 'p9/office/SELECT_OFFICE_LOOKUP_FAIL';
const SELECT_OFFICE_LOOKUP_SUCCESS = 'p9/office/SELECT_OFFICE_LOOKUP_SUCCESS';

const UPDATE_OFFICE = 'p9/office/UPDATE_OFFICE';
const UPDATE_OFFICE_FAIL = 'p9/office/UPDATE_OFFICE_FAIL';
const UPDATE_OFFICE_SUCCESS = 'p9/office/UPDATE_OFFICE_SUCCESS';

const initialState = {
  isLoading: false,
  isLoadingDefinitions: false,
  error: false,
  initialValues: {},
  officeDefinition: [],
  searchResults: null,
  currentOffice: null,
  officeLookup: null,
};

export default (state = initialState, action) => {
  switch (action.type) {
    case CLEAR_OFFICE:
    case HOME_BUTTON_CLICKED:
    case SEARCH_LISTINGS_PAGE_CLICKED:
      return initialState;
    case OFFICE_PAGE_CLICKED:
      return {
        ...state,
        currentOffice: null,
        searchResults: null,
      };
    case CLEAR_OFFICE_SEARCH:
      return {
        ...state,
        searchResults: null,
      };
    case FORM_SUBMITTED:
      return {
        ...state,
        currentOffice: null,
      };
    case LOAD_FORM_DEFINITION:
      return {
        ...state,
        officeLookup: null,
        searchResults: null,
      };
    case LOAD_OFFICE_DEFINITION:
      return {
        ...state,
        isLoadingDefinitions: true,
      };
    case LOAD_OFFICE_DEFINITION_FAIL:
      return {
        ...state,
        isLoadingDefinitions: false,
        error: action.payload,
      };
    case LOAD_OFFICE_DEFINITION_SUCCESS:
      return {
        ...state,
        ...action.payload,
        isLoadingDefinitions: false,
      };
    case OFFICE_LOOKUP:
    case SEARCH_OFFICES:
      return {
        ...state,
        isLoading: true,
      };
    case OFFICE_LOOKUP_FAIL:
    case SEARCH_OFFICES_FAIL:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case OFFICE_LOOKUP_SUCCESS:
    case SEARCH_OFFICES_SUCCESS:
      return {
        ...state,
        searchResults: action.payload,
        isLoading: false,
      };
    case SELECT_OFFICE:
    case SELECT_OFFICE_LOOKUP:
      return {
        ...state,
        isLoading: true,
      };
    case SELECT_OFFICE_FAIL:
    case SELECT_OFFICE_LOOKUP_FAIL:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case SELECT_OFFICE_LOOKUP_SUCCESS:
      return {
        ...state,
        isLoading: false,
        officeLookup: action.payload,
      };
    case SELECT_OFFICE_SUCCESS:
      return {
        ...state,
        currentOffice: action.payload,
        isLoading: false,
      };
    default:
      return state;
  }
};

const clearOffice = () => ({ type: CLEAR_OFFICE });

const clearOfficeSearch = () => ({ type: CLEAR_OFFICE_SEARCH });

// Derives initialValues object from form definition: [{},...] => {}
const deriveInitialValues = definition => {
  const values = {};
  const sorted = getSortedDefinition(definition);
  sorted.map(field => {
    // exclude office data from initial values
    if (field.layoutGroupType !== 'Photos') {
      values[field.FieldName] = '';
    }
    return null;
  });
  return values;
};

// retrieves office form definition via service call, then stashes results in Redux
const loadOfficeDefinition = () => async dispatch => {
  dispatch({ type: LOAD_OFFICE_DEFINITION });
  try {
    const { Data } = await ApiService.Office.getOfficeDefinition();
    const initialValues = deriveInitialValues(Data);
    const definition = transformDefinition(Data);
    dispatch({
      type: LOAD_OFFICE_DEFINITION_SUCCESS,
      payload: {
        initialValues,
        officeDefinition: definition,
      },
    });
  } catch (error) {
    console.error('Error: Office loadOfficeDefinition', error);
    dispatch({
      type: LOAD_OFFICE_DEFINITION_FAIL,
      payload: error,
    });
  }
};

// Preps search criteria for service call, stashes search results in Redux
const searchOffices = searchCriteria => async dispatch => {
  dispatch({ type: SEARCH_OFFICES });
  try {
    const criteriaList = [];
    Object.entries(searchCriteria).forEach(([name, value]) => {
      if (value !== '') {
        criteriaList.push({ name, value });
      }
    });

    // BE requires request body of this shape:
    const searchResults = await ApiService.Office.searchOffices({
      where: {
        clause: criteriaList,
      },
    });

    dispatch({
      type: SEARCH_OFFICES_SUCCESS,
      payload: searchResults,
    });
  } catch (error) {
    console.error('Error: Office searchOffices', error);
    dispatch({
      type: SEARCH_OFFICES_FAIL,
      payload: error,
    });
  }
};

const lookupOffices = searchCriteria => async dispatch => {
  dispatch({ type: OFFICE_LOOKUP });
  try {
    const criteriaList = [];
    Object.entries(searchCriteria).forEach(([name, value]) => {
      if (value !== '') {
        criteriaList.push({ name, value });
      }
    });

    // BE requires request body of this shape:
    const searchResults = await ApiService.Office.officeLookup({
      where: {
        clause: criteriaList,
      },
    });

    dispatch({
      type: OFFICE_LOOKUP_SUCCESS,
      payload: searchResults,
    });
  } catch (error) {
    console.error('Error: Office lookupOffices', error);
    dispatch({
      type: OFFICE_LOOKUP_FAIL,
      payload: error,
    });
  }
};

// Consumes office UID, preps for service call, retrieves selected office information & stashes in Redux
const setCurrentOffice = officeUid => async dispatch => {
  dispatch({ type: SELECT_OFFICE });
  try {
    const response = await ApiService.Office.selectOffice({
      where: {
        clause: [{ name: 'UniqueID', value: officeUid }],
      },
    });
    dispatch({
      type: SELECT_OFFICE_SUCCESS,
      payload: response.data[0],
    });
  } catch (error) {
    console.error('Error: Office setCurrentOffice', error);
    dispatch({
      type: SELECT_OFFICE_FAIL,
      payload: error,
    });
  }
};

// Consumes office UID, preps for service call, retrieves selected office information & stashes in Redux
const setOfficeLookup = officeUid => async dispatch => {
  dispatch({ type: SELECT_OFFICE_LOOKUP });
  try {
    const response = await ApiService.Office.selectOffice({
      where: {
        clause: [{ name: 'UniqueID', value: officeUid }],
      },
    });
    dispatch({
      type: SELECT_OFFICE_LOOKUP_SUCCESS,
      payload: response.data[0],
    });
  } catch (error) {
    console.error('Error: Office setOfficeLookup', error);
    dispatch({
      type: SELECT_OFFICE_LOOKUP_FAIL,
      payload: error,
    });
  }
};

// TODO: remove if no longer in use
const updateOffice = newOfficeData => async dispatch => {
  dispatch({ type: UPDATE_OFFICE });
  try {
    const response = await ApiService.Office.updateOffice(newOfficeData);
    dispatch({
      type: UPDATE_OFFICE_SUCCESS,
      payload: response,
    });
  } catch (error) {
    console.error('Error: Office updateOffice', error);
    dispatch({
      type: UPDATE_OFFICE_FAIL,
      payload: error,
    });
  }
};

export {
  clearOffice,
  clearOfficeSearch,
  loadOfficeDefinition,
  lookupOffices,
  searchOffices,
  setCurrentOffice,
  setOfficeLookup,
  updateOffice,
  SELECT_OFFICE_SUCCESS,
};
